import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/** The default fallback; a function component so it can use the app language. */
function ErrorFallback({ error, onRetry }: { error?: Error; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-nord-11/10">
        <AlertTriangle className="h-8 w-8 text-nord-11" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-nord-6">{t('uiKit.error.title', 'Something went wrong')}</h3>
        <p className="mt-1 text-xs text-nord-muted max-w-sm">
          {error?.message ?? t('uiKit.error.unexpected', 'An unexpected error occurred')}
        </p>
      </div>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-lg bg-nord-2 px-4 py-2 text-sm font-medium text-nord-4 hover:bg-nord-3 transition-colors"
      >
        <RefreshCw className="h-4 w-4" />
        {t('uiKit.error.retry', 'Try again')}
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}
